import bannerDispatcher from '../action/banner';
import {
  COLUMN_BANNER,
  BANNER_STATUS,
  BANNER_STATUS_COLORS,
  TYPE_MODAL,
} from '../constants';
import ModalAddOrEditBanner from './ModalAddOrEditBanner';
import { CreateNewIcon } from '@/assets/svg';
import {
  ActivateIcon,
  CloseCircle,
  CloseIcon,
  DeactivateIcon,
  DeleteModalIcon,
  EditIcon,
  EyeHideIcon,
  EyeIcon,
} from '@/assets/svg';
import Image from '@/components/Image';
import { momentFormatted } from '@/helpers';
import { StatusComponent } from '@/module/all-screenings/component/StatusComponent';
import TableCellFilted from '@/module/common/componentUI/TableCellFilted';
import CustomMenu from '@/new-components/CustomMenu';
import CustomMenuActions from '@/new-components/CustomMenuActions';
import ModalConfirmation from '@/new-components/CustomModal/ModalConfirmation';
import CustomPaperContainer from '@/new-components/CustomPaperContainer';
import CustomHeader from '@/new-components/CustomPaperContainer/CustomHeader';
import CustomTable from '@/new-components/CustomTable';
import CustomTooltip from '@/new-components/CustomTooltip';
import {
  Button,
  Dialog,
  IconButton,
  TableCell,
  TableHead,
  TableRow,
  DialogContent,
  Box,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { MoreVertRounded } from '@material-ui/icons';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const CustomDialogContent = withStyles({
  root: {
    padding: 40,
    position: 'relative',
    '& .icon': {
      position: 'absolute',
      top: 10,
      right: 10,
    },

    '&:first-child': {
      paddingTop: 40,
    },
  },
})(DialogContent);

const PreviewContent = withStyles({
  root: {
    width: 160,
    height: 85.6,

    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(51,51,51,0.4)',
    borderRadius: 8,

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
})(Box);

const Banner = () => {
  const { list: data, paging, loading } = useSelector((state) => state.banner);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState({});
  const [typeModal, setTypeModal] = useState(null);

  const [searchKey, setSearchKey] = useState('');

  const [showDeleteModal, setDeleteShowModal] = useState(false);
  const [showActiveModal, setShowActiveModal] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [isHovered, setIsHovered] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [imageSelected, setImageSelected] = useState('');

  const handleMouseEnter = (url) => {
    setImageSelected(url);
    setIsHovered(true);
  };
  const handleMouseLeave = () => setIsHovered(false);

  const handlePreviewOpen = (url) => {
    setImageSelected(url);
    setPreviewOpen(true);
    handleMouseLeave();
  };
  const handlePreviewClose = () => {
    setImageSelected('');
    setPreviewOpen(false);
  };

  const onShowModal = (type) => {
    setShowModal(true);
    setTypeModal(type);
  };

  const onCloseModal = () => {
    setShowModal(false);
    setTypeModal(null);
  };

  const onDeleteBanner = () => {
    bannerDispatcher.deleteBanner(selectedItem.id, () => {
      fetchData();
      setDeleteShowModal(false);
    });
  };

  const handleSubmitBanner = (formValue) => {
    bannerDispatcher.setState('loading', true);
    if (typeModal === TYPE_MODAL.Create) {
      bannerDispatcher.createBanner(formValue, () => {
        fetchData();
        setShowModal(false);
        setTypeModal(null);
      });
    } else if (typeModal === TYPE_MODAL.Edit) {
      bannerDispatcher.editBanner(
        { id: selectedItem.id, banner: formValue },
        () => {
          fetchData();
          setShowModal(false);
          setTypeModal(null);
        }
      );
    }
  };

  const onChangeBannerStatus = () => {
    const { isActive } = selectedItem;
    bannerDispatcher.editBanner(
      { id: selectedItem.id, banner: { ...selectedItem, isActive: !isActive } },
      () => {
        setAnchorEl(null);
        fetchData();
        setShowActiveModal(false);
      }
    );
  };

  const fetchData = async (search = searchKey, pagingData = paging) => {
    bannerDispatcher.getData(search, pagingData);
  };

  const debounceLoadData = useCallback(
    debounce((searchKey, paging) => {
      fetchData(searchKey, {
        pageIndex: 0,
        pageSize: paging.pageSize,
      });
    }, 500),
    []
  );

  useEffect(() => {
    debounceLoadData(searchKey, paging);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey]);

  const TableHeader = () => (
    <TableHead>
      <TableRow>
        {COLUMN_BANNER.map((item) => (
          <TableCellFilted
            key={item.stateValue}
            label={item.label}
            stateValue={item.stateValue}
            style={{
              minWidth: item.minWidth || 'unset',
              width: item.width || 'unset',
            }}
            isHidden={false}
          />
        ))}
      </TableRow>
    </TableHead>
  );

  const renderTableBody = (row) => {
    return (
      <>
        <TableCell>{row.order}</TableCell>
        <TableCell>
          <CustomTooltip content={row.name} />
        </TableCell>
        <TableCell>
          {row.url && (
            <Box
              width="100%"
              position="relative"
              onMouseEnter={() => handleMouseEnter(row.url)}
              onMouseLeave={handleMouseLeave}
            >
              <Image
                src={row.url}
                alt="Preview"
                style={{ borderRadius: 8, width: 160, height: 85.6 }}
              />
              {isHovered && imageSelected === row.url && (
                <PreviewContent onClick={() => handlePreviewOpen(row.url)}>
                  <EyeIcon color="white" width={24} height={24} />
                </PreviewContent>
              )}
            </Box>
          )}
        </TableCell>
        <TableCell>
          <StatusComponent
            status={row.isActive ? 'Active' : 'Inactive'}
            filterList={BANNER_STATUS}
            filterListColor={BANNER_STATUS_COLORS}
          />
        </TableCell>
        <TableCell>{momentFormatted(row.startDate)}</TableCell>
        <TableCell>{momentFormatted(row.endDate)}</TableCell>
        <TableCell>
          <IconButton
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
              setSelectedItem(row);
            }}
          >
            <MoreVertRounded />
          </IconButton>
        </TableCell>
      </>
    );
  };

  const renderListActions = (selectedItem) => {
    if (!selectedItem) return [];
    const isActive = selectedItem?.isActive;
    return [
      {
        key: `mark-as-${isActive ? 'inactive' : 'active'}`,
        icon: isActive ? EyeHideIcon : EyeIcon,
        label: `Mark as ${isActive ? 'inactive' : 'active'}`,
        onClick: () => {
          setAnchorEl(null);
          setSelectedItem(selectedItem);
          setShowActiveModal(true);
        },
      },
      {
        key: 'edit-banner',
        icon: EditIcon,
        label: 'Edit banner',
        styles: { width: 18, height: 18 },
        onClick: () => {
          setAnchorEl(null);
          onShowModal(TYPE_MODAL.Edit);
        },
      },
      {
        key: 'delete-banner',
        icon: CloseCircle,
        label: 'Delete banner',
        styles: { width: 18, height: 18 },
        color: '#DB3D49',
        onClick: () => {
          setAnchorEl(null);
          setSelectedItem(selectedItem);
          setDeleteShowModal(true);
        },
      },
    ];
  };

  return (
    <>
      <CustomMenu
        id="simple-menu"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <CustomMenuActions listActions={renderListActions(selectedItem)} />
      </CustomMenu>
      <CustomPaperContainer
        header={
          <CustomHeader
            searchPlaceholder="Search by URL..."
            title="Banners"
            setSearchKey={setSearchKey}
            searchKey={searchKey}
            fetchData={fetchData}
            renderButton={() => (
              <>
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<CreateNewIcon />}
                  onClick={() => {
                    setSelectedItem(null);
                    onShowModal(TYPE_MODAL.Create);
                  }}
                >
                  Create new
                </Button>
              </>
            )}
          />
        }
      >
        <CustomTable
          data={data}
          paging={paging}
          header={TableHeader}
          renderRow={(row, i) => renderTableBody(row)}
          noDataHelperText="No banners"
          noDataHelperSubText="Please create a new banner."
          totalCols={COLUMN_BANNER.length}
          fetchData={fetchData}
          style={{ height: 'calc(100vh - 207px)' }}
        />
      </CustomPaperContainer>

      {showModal && (
        <ModalAddOrEditBanner
          open={showModal}
          onClose={onCloseModal}
          typeModal={typeModal}
          onSubmit={handleSubmitBanner}
          selectedItem={selectedItem}
          loading={loading}
          totalItem={paging.totalItem}
        />
      )}

      {showDeleteModal && (
        <ModalConfirmation
          open={showDeleteModal}
          onClick={onDeleteBanner}
          onClose={() => setDeleteShowModal(false)}
          mainContent="Delete banner"
          subContent="Are you sure you want to delete this banner?"
          cancelLabel="Cancel"
          confirmLabel="Delete"
          confirmColor="red"
          Icon={<DeleteModalIcon />}
        />
      )}

      {showActiveModal && (
        <ModalConfirmation
          open={showActiveModal}
          onClick={onChangeBannerStatus}
          onClose={() => setShowActiveModal(false)}
          mainContent={`Mark as ${
            selectedItem.isActive ? 'inactive' : 'active'
          }`}
          subContent={`Are you sure you want to mark this banner as ${
            selectedItem.isActive ? 'inactive' : 'active'
          }?`}
          cancelLabel="Cancel"
          confirmLabel={'Confirm'}
          Icon={selectedItem.isActive ? <DeactivateIcon /> : <ActivateIcon />}
        />
      )}

      {previewOpen && (
        <Dialog
          disableBackdropClick
          open={previewOpen}
          onClose={handlePreviewClose}
          maxWidth="lg"
        >
          <CustomDialogContent>
            <IconButton className="icon" onClick={handlePreviewClose}>
              <CloseIcon />
            </IconButton>

            <Image src={imageSelected} alt="Preview" />
          </CustomDialogContent>
        </Dialog>
      )}
    </>
  );
};

export default Banner;
